import { useReducer, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import type {
  AIState,
  AIAction,
  AIContextValue,
  AIOption,
  AIValidationResult,
  AISubCategory,
  Layer,
  TemplateConfig,
  LayoutVariant,
  ToggleState,
} from '@/types';
import {
  AI_STEP_ORDER,
  MAX_ELEMENTS,
  MAX_TEXT_LENGTH,
  MAX_CUSTOM_PROMPT_LENGTH,
  MAX_NOUN_LENGTH,
} from '@/types';
import { AIContext } from './ai-context';
import { generateDesign, FAL_ERROR_MESSAGES } from '@/services/fal';
import { saveDesign } from '@/services/storage';
import { applyGrayscale } from '@/utils/imageFilters';
import type { Side } from './sideContext';
import { useEditor } from './useEditor';
import { useAuth } from './useAuth';
import { AI_IMAGE_WIDTH, AI_IMAGE_HEIGHT, centerOnCanvas } from '@/utils/canvasConstants';

// SessionStorage keys for persisting AI state during auth flows
const AI_STATE_FRONT_KEY = 'ai_design_state_front';
const AI_STATE_BACK_KEY = 'ai_design_state_back';

const initialToggles: ToggleState = {
  upload: false,
  text: false,
  elements: false,
};

const initialState: AIState = {
  style: null,
  template: null,
  selectedLayout: null,
  mood: null,
  elements: [],
  nouns: {},
  text: '',
  uploadedImage: null,
  currentStep: 'template',
  toggles: initialToggles,
  isGenerating: false,
  generatedImageUrl: null,
  compositeImageUrl: null,
  error: null,
  isSaving: false,
  saveError: null,
};

/**
 * Load AI state from sessionStorage
 * Returns null if no saved state or if state is corrupted
 */
function loadStateFromSession(key: string): AIState | null {
  try {
    const saved = sessionStorage.getItem(key);
    if (!saved) return null;

    const parsed = JSON.parse(saved) as AIState;

    // Validate that parsed state has required structure
    if (typeof parsed !== 'object' || parsed === null) return null;

    // Migration: ensure toggles exist (backwards compatibility)
    if (!parsed.toggles) {
      parsed.toggles = initialToggles;
    }

    // Migration: ensure selectedLayout exists (backwards compatibility)
    if (parsed.selectedLayout === undefined) {
      parsed.selectedLayout = null;
    }

    // Migration: ensure nouns exist (backwards compatibility)
    if (!parsed.nouns) {
      parsed.nouns = {};
    }

    return parsed;
  } catch (error) {
    console.warn('[AIContext] Failed to load state from session storage:', error);
    return null;
  }
}

/**
 * Save AI state to sessionStorage
 */
function saveStateToSession(key: string, state: AIState): void {
  try {
    // Only persist design selections, not transient state like isGenerating
    const persistedState: AIState = {
      style: state.style,
      template: state.template,
      selectedLayout: state.selectedLayout,
      mood: state.mood,
      elements: state.elements,
      nouns: state.nouns,
      text: state.text,
      uploadedImage: state.uploadedImage,
      currentStep: state.currentStep,
      toggles: state.toggles,
      // Don't persist transient state
      isGenerating: false,
      generatedImageUrl: null,
      compositeImageUrl: null,
      error: null,
      isSaving: false,
      saveError: null,
    };

    sessionStorage.setItem(key, JSON.stringify(persistedState));
  } catch (error) {
    console.warn('[AIContext] Failed to save state to session storage:', error);
  }
}

/**
 * Clear AI state from sessionStorage
 */
function clearStateFromSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn('[AIContext] Failed to clear state from session storage:', error);
  }
}

function aiReducer(state: AIState, action: AIAction): AIState {
  switch (action.type) {
    case 'SET_STYLE':
      return { ...state, style: action.payload, error: null };
    case 'SET_TEMPLATE':
      return { ...state, template: action.payload, selectedLayout: null, nouns: {}, error: null };
    case 'SET_LAYOUT':
      return { ...state, selectedLayout: action.payload, nouns: {}, error: null };
    case 'SET_MOOD':
      return { ...state, mood: action.payload, error: null };
    case 'ADD_ELEMENT':
      if (state.elements.length >= MAX_ELEMENTS) return state;
      return { ...state, elements: [...state.elements, action.payload], error: null };
    case 'REMOVE_ELEMENT':
      return {
        ...state,
        elements: state.elements.filter((_, i) => i !== action.payload),
        error: null,
      };
    case 'SET_NOUN': {
      const { slotId, value } = action.payload;
      return {
        ...state,
        nouns: { ...state.nouns, [slotId]: value.slice(0, MAX_NOUN_LENGTH) },
        error: null,
      };
    }
    case 'SET_TEXT': {
      const maxLen = state.template?.customPromptMaxLength
        ? MAX_CUSTOM_PROMPT_LENGTH
        : MAX_TEXT_LENGTH;
      return {
        ...state,
        text: action.payload.slice(0, maxLen),
        error: null,
      };
    }
    case 'SET_UPLOADED_IMAGE':
      return { ...state, uploadedImage: action.payload, error: null };
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_TOGGLE': {
      const { toggle, value } = action.payload;
      const nextState = {
        ...state,
        toggles: { ...state.toggles, [toggle]: value },
        error: null,
      };
      // Clear associated data when toggle is turned off
      if (!value) {
        if (toggle === 'text') nextState.text = '';
        if (toggle === 'upload') nextState.uploadedImage = null;
        if (toggle === 'elements') nextState.elements = [];
      }
      return nextState;
    }
    case 'START_GENERATING':
      return { ...state, isGenerating: true, error: null };
    case 'GENERATION_SUCCESS':
      return {
        ...state,
        isGenerating: false,
        generatedImageUrl: action.payload,
        error: null,
      };
    case 'GENERATION_ERROR':
      return { ...state, isGenerating: false, error: action.payload };
    case 'COMPOSITE_READY':
      return { ...state, compositeImageUrl: action.payload };
    case 'START_SAVING':
      return { ...state, isSaving: true, saveError: null };
    case 'SAVE_SUCCESS':
      return { ...state, isSaving: false, saveError: null };
    case 'SAVE_ERROR':
      return { ...state, isSaving: false, saveError: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export interface AIProviderProps {
  children: ReactNode;
  /** The active side (front or back) - determines which state to expose */
  activeSide: Side;
}

export function AIProvider({ children, activeSide }: AIProviderProps) {
  // Maintain TWO separate AI state instances with sessionStorage persistence
  // This ensures design progress is preserved during auth flows
  const [frontState, frontDispatch] = useReducer(
    aiReducer,
    initialState,
    () => loadStateFromSession(AI_STATE_FRONT_KEY) || initialState
  );
  const [backState, backDispatch] = useReducer(
    aiReducer,
    initialState,
    () => loadStateFromSession(AI_STATE_BACK_KEY) || initialState
  );

  // Access EditorContext to add generated images as canvas layers
  const editor = useEditor();

  // Access AuthContext to get the current user for cloud sync
  const { user } = useAuth();

  // Persist front state to sessionStorage whenever it changes
  useEffect(() => {
    saveStateToSession(AI_STATE_FRONT_KEY, frontState);
  }, [frontState]);

  // Persist back state to sessionStorage whenever it changes
  useEffect(() => {
    saveStateToSession(AI_STATE_BACK_KEY, backState);
  }, [backState]);

  // Expose the active one based on activeSide prop:
  const state = activeSide === 'front' ? frontState : backState;
  const dispatch = activeSide === 'front' ? frontDispatch : backDispatch;

  const setStyle = useCallback(
    (style: AIOption | null) => {
      dispatch({ type: 'SET_STYLE', payload: style });
    },
    [dispatch]
  );

  const setTemplate = useCallback(
    (template: TemplateConfig) => {
      dispatch({ type: 'SET_TEMPLATE', payload: template });
    },
    [dispatch]
  );

  const setLayout = useCallback(
    (layout: LayoutVariant | null) => {
      dispatch({ type: 'SET_LAYOUT', payload: layout });
    },
    [dispatch]
  );

  const setMood = useCallback(
    (mood: AIOption | null) => {
      dispatch({ type: 'SET_MOOD', payload: mood });
    },
    [dispatch]
  );

  const addElement = useCallback(
    (element: string) => {
      dispatch({ type: 'ADD_ELEMENT', payload: element });
    },
    [dispatch]
  );

  const removeElement = useCallback(
    (index: number) => {
      dispatch({ type: 'REMOVE_ELEMENT', payload: index });
    },
    [dispatch]
  );

  const setNoun = useCallback(
    (slotId: string, value: string) => {
      dispatch({ type: 'SET_NOUN', payload: { slotId, value } });
    },
    [dispatch]
  );

  const setText = useCallback(
    (text: string) => {
      dispatch({ type: 'SET_TEXT', payload: text });
    },
    [dispatch]
  );

  const setUploadedImage = useCallback(
    (image: string | null) => {
      dispatch({ type: 'SET_UPLOADED_IMAGE', payload: image });
    },
    [dispatch]
  );

  const setToggle = useCallback(
    (toggle: keyof ToggleState, value: boolean) => {
      dispatch({ type: 'SET_TOGGLE', payload: { toggle, value } });
    },
    [dispatch]
  );

  const goToStep = useCallback(
    (step: AISubCategory) => {
      dispatch({ type: 'SET_STEP', payload: step });
    },
    [dispatch]
  );

  const goToNextStep = useCallback(() => {
    const currentIndex = AI_STEP_ORDER.indexOf(state.currentStep);
    const nextStep = AI_STEP_ORDER[currentIndex + 1];
    if (currentIndex < AI_STEP_ORDER.length - 1 && nextStep) {
      dispatch({ type: 'SET_STEP', payload: nextStep });
    }
  }, [state.currentStep, dispatch]);

  const validate = useCallback((): AIValidationResult => {
    const missingFields: ('style' | 'template' | 'mood')[] = [];
    if (!state.style) missingFields.push('style');
    if (!state.template) missingFields.push('template');
    if (!state.mood) missingFields.push('mood');
    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  }, [state.style, state.template, state.mood]);

  const generate = useCallback(
    async (
      productImageUrl: string,
      imageSize?: { width: number; height: number },
      displaySize?: { width: number; height: number }
    ) => {
      const validation = validate();
      if (!validation.isValid) {
        dispatch({
          type: 'GENERATION_ERROR',
          payload: `Please select: ${validation.missingFields.join(', ')}`,
        });
        return;
      }

      if (!state.style || !state.template || !state.mood) {
        dispatch({
          type: 'GENERATION_ERROR',
          payload: 'Missing required selections',
        });
        return;
      }

      // Before generating, save current canvas to library if it has user layers
      // This preserves the old design before replacing it with the new AI generation
      if (editor.hasUserLayers()) {
        console.warn(
          '[AIContext] Canvas has user layers, should save to library before generating'
        );
        // TODO: Implement canvas export and save logic
        // For now, we'll just log and continue with generation
        // Components will need to implement blob export themselves
      }

      dispatch({ type: 'START_GENERATING' });

      try {
        const result = await generateDesign({
          productImageUrl,
          // Only pass the URL if it's a remote URL (not a local blob: preview)
          referenceImageUrl:
            state.uploadedImage && !state.uploadedImage.startsWith('blob:')
              ? state.uploadedImage
              : undefined,
          style: state.style.label,
          template: state.template, // Pass full template config
          mood: state.mood.label,
          elements: state.elements,
          nouns: state.nouns,
          text: state.text,
          selectedLayout: state.selectedLayout || undefined, // Layout variant (if any)
          imageSize, // Product-specific dimensions for Fal.ai
        });

        console.warn('[AIContext] Generation result:', result);

        if (result.hasNsfwContent) {
          console.warn('[AIContext] NSFW content detected');
          // Throw to trigger GENERATION_ERROR dispatch in catch block AND
          // credit refund in handleConfirmGenerate's catch block
          throw new Error(
            'Content safety filter flagged this design. Try different elements, text, or mood.'
          );
        }

        console.warn('[AIContext] Dispatching GENERATION_SUCCESS with URL:', result.imageUrl);
        dispatch({
          type: 'GENERATION_SUCCESS',
          payload: result.imageUrl,
        });

        // Convert fal.ai URL to data URL to ensure it works in project thumbnails
        // (External URLs may expire or fail CORS checks when generating thumbnails later)
        let aiImageDataUrl = result.imageUrl;
        try {
          const imageResponse = await fetch(result.imageUrl);
          const imageBlob = await imageResponse.blob();
          aiImageDataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to convert image to data URL'));
            reader.readAsDataURL(imageBlob);
          });
          console.warn('[AIContext] Converted AI image to data URL for reliable storage');
        } catch (conversionError) {
          console.warn('[AIContext] Failed to convert AI image to data URL:', conversionError);
          // Don't add a broken layer — surface the error to the user
          dispatch({
            type: 'GENERATION_ERROR',
            payload: 'Design generated but failed to load the image. Please try again.',
          });
          return;
        }

        // Convert AI-generated image to grayscale (laser engraver only produces white/gray on black)
        try {
          const grayscaleResult = await applyGrayscale(aiImageDataUrl, {
            brightness: 0,
            contrast: 0,
          });
          aiImageDataUrl = grayscaleResult.dataUrl;
          console.warn('[AIContext] Converted AI image to grayscale for laser engraving');
        } catch (grayscaleError) {
          console.warn(
            '[AIContext] Failed to convert AI image to grayscale, using original:',
            grayscaleError
          );
        }

        // Add generated image as a canvas layer (2nd from bottom, covering product base)
        // Layer is locked by default - users can unlock via Layers panel if needed
        // Use display dimensions (engravable area) for canvas placement, not generation dimensions
        const aiWidth = displaySize?.width ?? imageSize?.width ?? AI_IMAGE_WIDTH;
        const aiHeight = displaySize?.height ?? imageSize?.height ?? AI_IMAGE_HEIGHT;
        const { width: canvasW, height: canvasH } = editor.state.canvas;
        const aiPosition = centerOnCanvas(aiWidth, aiHeight, canvasW, canvasH);
        const aiLayer: Layer = {
          id: `ai-layer-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name: 'AI Generated',
          type: 'image',
          visible: true,
          locked: true,
          inverted: false,
          opacity: 1,
          data: {
            type: 'image',
            src: aiImageDataUrl,
            x: aiPosition.x,
            y: aiPosition.y,
            width: aiWidth,
            height: aiHeight,
            rotation: 0,
            flipH: false,
            flipV: false,
          },
        };

        editor.addLayer(aiLayer);
        // Deselect the layer so it doesn't show selection handles
        editor.setActiveLayer(null);
        console.warn('[AIContext] Added AI-generated layer to canvas (locked, deselected)');

        // Note: Frame overlay is handled by EditorContext's product-overlay-layer
        // which is created in createProductLayers()
      } catch (error) {
        // Map API errors to user-friendly messages
        let errorMessage = 'Generation failed';

        if (error instanceof Error) {
          // Check for API error with status code in message
          const apiErrorMatch = error.message.match(/status.*?(\d{3})/i);
          if (apiErrorMatch && apiErrorMatch[1]) {
            const statusCode = parseInt(apiErrorMatch[1], 10);
            errorMessage = (FAL_ERROR_MESSAGES[statusCode] as string | undefined) || error.message;
          } else if (error.message.includes('API key')) {
            errorMessage =
              (FAL_ERROR_MESSAGES[401] as string | undefined) || 'Unable to connect to AI service';
          } else if (error.message.includes('timeout') || error.message.includes('timed out')) {
            errorMessage =
              (FAL_ERROR_MESSAGES.timeout as string | undefined) ||
              'Generation taking longer than expected';
          } else {
            errorMessage = error.message;
          }
        }

        dispatch({
          type: 'GENERATION_ERROR',
          payload: errorMessage,
        });

        // Re-throw so callers (e.g., handleConfirmGenerate) can trigger credit refunds
        throw error;
      }
    },
    [
      validate,
      state.style,
      state.template,
      state.selectedLayout,
      state.mood,
      state.uploadedImage,
      state.elements,
      state.nouns,
      state.text,
      dispatch,
      editor,
    ]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    // Clear sessionStorage for the active side when user explicitly resets
    const storageKey = activeSide === 'front' ? AI_STATE_FRONT_KEY : AI_STATE_BACK_KEY;
    clearStateFromSession(storageKey);
  }, [dispatch, activeSide]);

  const saveToLibrary = useCallback(
    async (compositeDataUrl: string, productId: string): Promise<boolean> => {
      if (!state.style || !state.template) {
        dispatch({
          type: 'SAVE_ERROR',
          payload: 'Missing design information',
        });
        return false;
      }

      dispatch({ type: 'START_SAVING' });

      try {
        // Convert data URL to Blob
        const response = await fetch(compositeDataUrl);
        const blob = await response.blob();

        // Create StoredDesign object
        // Designs are saved separately for front and back sides
        const design = {
          id: crypto.randomUUID(),
          userId: user?.id, // Link to user for cloud sync (optional for local-only saves)
          frontBlob: activeSide === 'front' ? blob : null,
          backBlob: activeSide === 'back' ? blob : null,
          createdAt: new Date(),
          metadata: {
            productId,
            frontStyle: activeSide === 'front' ? state.style.label : '',
            frontTemplate: activeSide === 'front' ? state.template.label : '',
            backStyle: activeSide === 'back' ? state.style.label : undefined,
            backTemplate: activeSide === 'back' ? state.template.label : undefined,
          },
        };

        // Save to IndexedDB
        await saveDesign(design);

        dispatch({ type: 'SAVE_SUCCESS' });

        // Clear sessionStorage after successful save - design is now persisted to IndexedDB
        const storageKey = activeSide === 'front' ? AI_STATE_FRONT_KEY : AI_STATE_BACK_KEY;
        clearStateFromSession(storageKey);
        return true;
      } catch (error) {
        console.error('Failed to save design:', error);
        dispatch({
          type: 'SAVE_ERROR',
          payload: error instanceof Error ? error.message : 'Failed to save design',
        });
        return false;
      }
    },
    [state.style, state.template, dispatch, activeSide, user]
  );

  const value: AIContextValue = useMemo(
    () => ({
      state,
      dispatch,
      setStyle,
      setTemplate,
      setLayout,
      setMood,
      addElement,
      removeElement,
      setNoun,
      setText,
      setUploadedImage,
      setToggle,
      goToStep,
      goToNextStep,
      validate,
      generate,
      saveToLibrary,
      reset,
    }),
    [
      state,
      dispatch,
      setStyle,
      setTemplate,
      setLayout,
      setMood,
      addElement,
      removeElement,
      setNoun,
      setText,
      setUploadedImage,
      setToggle,
      goToStep,
      goToNextStep,
      validate,
      generate,
      saveToLibrary,
      reset,
    ]
  );

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}
